import { Divider, Typography } from "@mui/joy";
import { Box } from "@mui/system";
import {
  DataGridPremium as DataGridPro,
  GridColumns,
  GridToolbarExport,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Timer } from "../../entities";
import { kronosSecondsSplice } from "../../helpers";
import {
  setStagedTimer,
  setTimerFormOpen,
  timerFiltersSelector,
} from "../../redux";
import {
  useKronosTasks,
  useKronosTimers,
} from "../../../redux/kronos/kronosHooks";
import { KronosTimerDuration } from "../kronosTimerDuration";
import { TimerSearch } from "./timerSearch";

/**
 *
 * @returns {ReactElement} TimersDataGrid component
 */
export function TimersDataGrid({
  userID,
  projectID,
  searchEnabled = true,
  timers,
  autoHeight,
  header = true,
  forDate,
}: {
  userID?: number;
  projectID?: number;
  searchEnabled?: boolean;
  timers?: Timer[];
  autoHeight?: boolean;
  header?: boolean;
  forDate?: string;
}) {
  const apiRef = useGridApiRef();
  const timerFilters = useSelector(timerFiltersSelector);
  const timerQueryArgs =
    userID || projectID || timers
      ? undefined
      : Array.isArray(timerFilters)
      ? timerFilters.length
        ? { filters: JSON.stringify(timerFilters) }
        : undefined
      : (timerFilters as Record<string, unknown> | undefined);
  const { timers: fetchedTimers } = useKronosTimers(timerQueryArgs);
  const { tasks } = useKronosTasks();
  const dispatch = useDispatch();
  const _timers = (timers ?? fetchedTimers).filter((timer) => {
    if (userID && timer.userID !== userID) return false;
    if (projectID && timer.projectID !== projectID) return false;
    return timer.completed();
  });

  const columns: GridColumns<Timer> = [
    {
      field: "id",
      headerName: "ID",
      width: 50,
    },
    {
      field: "projectName",
      headerName: "Project",
      width: 200,
    },
    {
      field: "userName",
      headerName: "User",
      width: 200,
    },
    {
      field: "taskID",
      headerName: "Task",
      width: 200,
      valueGetter: (value) => tasks.find((t) => t.id === value)?.name ?? "",
      renderCell: (p) => {
        const task = tasks.find((t) => t.id === p.row.taskID);
        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box
              sx={{
                background: task?.color,
                height: 16,
                width: 16,
                borderRadius: "2px",
              }}
            />
            <Typography level="body2">{p.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: "startDateUTC",
      headerName: "Date",
      flex: 1,
      valueGetter: (_value, row) => {
        if (!row) return "";
        return `${moment(row.start(forDate)).format("MM/DD hh:mm A")}${
          row.endDateUTC
            ? ` - ${moment(row.end(forDate)).format("MM/DD hh:mm A")}`
            : ""
        }`;
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 100,
      align: "right",
      valueGetter: (_value, row) => row?.durationLabel(forDate) ?? "",
      renderCell: (p) => (
        <KronosTimerDuration forDate={forDate} timer={p.row} />
      ),
    },
  ];

  return (
    <>
      <DataGridPro
        rows={_timers}
        autoHeight={autoHeight}
        rowSelection={false}
        cellSelection
        disableClipboardPaste={false}
        clipboardCopyCellDelimiter="\t"
        disableRowSelectionOnClick
        columns={columns}
        onRowClick={(p) => {
          dispatch(setStagedTimer(p.row));
          dispatch(setTimerFormOpen(true));
        }}
        experimentalFeatures={{ newEditingApi: true }}
        apiRef={apiRef}
        density="compact"
        getCellClassName={(p) => (!p.isEditable ? "read-only" : "")}
        componentsProps={{
          header: {
            searchEnabled,
          },
          footer: {
            rows: _timers,
            forDate,
          },
        }}
        components={{
          Footer: CustomFooter,
          Header: header ? CustomHeader : undefined,
        }}
        initialState={{
          pinnedColumns: {
            left: ["id"],
          },
        }}
      />
    </>
  );
}

function CustomHeader({ searchEnabled }: { searchEnabled: boolean }) {
  return (
    <Box sx={{ display: "flex", gap: 1, p: 1, position: "relative" }}>
      {searchEnabled ? <TimerSearch /> : []}
      <GridToolbarExport />
    </Box>
  );
}

function CustomFooter({ rows, forDate }: { rows: Timer[]; forDate?: string }) {
  let totalDuration = 0;

  rows.forEach((r) => (totalDuration += r.durationInSeconds(forDate)));
  return (
    <>
      <Divider />
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          gap: 1,
        }}
      >
        <Typography level="body2">Total: </Typography>
        <KronosTimerDuration splice={kronosSecondsSplice(totalDuration)} />
      </Box>
    </>
  );
}
